import { createContext, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import parse from 'html-react-parser';
import { fetchAndParse } from '../lib/fetchAndParse';
import staticTranslations from '../staticTranslations.json';

import { updateMetaTags } from '../lib/i18next';

const GlobalContext = createContext();

export const GlobalProvider = ({ children }) => {
  const { t, i18n } = useTranslation();
  const iUrl = import.meta.env.VITE_DIRECTUS_PUBLIC_URL + "/assets/";
  const [topbarStatus, setTopbarStatus] = useState({});
  const [directusGlobals, setDirectusGlobals] = useState({});
  const [isLoading, setIsLoading] = useState(true);


  useEffect(() => {
    const loadData = async () => {
      try {
        // Load content and fonts in parallel
        const [contentResult, fontsLoaded] = await Promise.all([
          fetchAndParse(),
          loadFonts()
        ]);

        const { parsedContent, topbarStatus, directusGlobals } = contentResult;
        Object.keys(parsedContent).forEach(lang => {
          const mergedTranslations = {
            ...staticTranslations[lang],
            ...parsedContent[lang]
          }
          i18n.addResourceBundle(lang, 'translation', mergedTranslations, true, true);
        });
        setTopbarStatus(topbarStatus);
        setDirectusGlobals(directusGlobals)
        setIsLoading(false);
      } catch (error) {
        console.error('Failed to load content or fonts:', error);
        setIsLoading(false);
      }
    };

    const loadFonts = async () => {
      if (!('fonts' in document)) {
        // Fallback for browsers without Font Loading API
        return new Promise(resolve => setTimeout(resolve, 1000));
      }

      try {
        // Wait for the specific fonts we're using
        await Promise.all([
          document.fonts.load('400 16px "Cormorant Garamond"'),
          document.fonts.load('700 16px "Cormorant Garamond"'),
          document.fonts.load('400 16px "Inter"'),
          document.fonts.load('500 16px "Inter"')
        ]);

        // Ensure all fonts are ready
        await document.fonts.ready;
        return true;
      } catch (error) {
        console.warn('Font loading failed, continuing anyway:', error);
        return true;
      }
    };
    loadData();
  }, [i18n]);

  useEffect(() => {
    updateMetaTags(t);
  }, [t]);

  const value = {
    t,
    i18n,
    iUrl,
    parse,
    topbarStatus,
    isLoading,
    directusGlobals,
  };

  return (
    <GlobalContext.Provider value={value}>
      {children}
    </GlobalContext.Provider>
  );
};

export const useGlobal = () => {
  const context = useContext(GlobalContext);
  if (!context) {
    throw new Error('useGlobal must be used within a GlobalProvider');
  }
  return context;
};
